import { Box, Button, Heading, Icon, Modal, Tag, Text } from '@zillow/constellation-10';
import { IconEditFilled } from '@zillow/constellation-icons';

import { FeeApplicableLevelType } from '@zg-rentals/ts-rental-manager-api-client';

import { getConstellationIcon } from '../../../utils/iconUtils';

import { categoryValueStyles, detailLabelStyles, detailValueStyles } from './styles';

import { EVENT_IDS } from '../../../analytics/events/multiFamilyCostsAndFees';

import type React from 'react';
import type { GetMultifamilyFeesDictionaryItemsDto, MultifamilyFeeDto } from '@zg-rentals/ts-rental-manager-api-client';

const DEFAULT_APPLICABLE_LEVEL_DISPLAY = 'Property';

type MultifamilyViewFeeDetailsModalProps = {
  onClose: () => void;
  fee: MultifamilyFeeDto;
  dictionaryItems: GetMultifamilyFeesDictionaryItemsDto;
  onEditFee: (fee: MultifamilyFeeDto) => void;
  isEditFeeDisabled: boolean;
};

const getFeeNameDisplay = (fee: MultifamilyFeeDto, dictionaryItems: GetMultifamilyFeesDictionaryItemsDto) => {
  if (fee?.isImportedFromFeed && fee?.feeDisplayName) {
    return fee.feeDisplayName;
  }

  return fee?.feeName || dictionaryItems?.feeCategory[fee?.feeCategory]?.feeType?.[fee?.feeType]?.displayName || '';
};

const getDisplayValue = (dictionary: Record<string, { displayName: string }> | undefined, key: string) => {
  return key && dictionary && dictionary[key] ? dictionary[key].displayName : '';
};

const getFloorPlanOrUnitDisplay = (fee: MultifamilyFeeDto, dictionaryItems: GetMultifamilyFeesDictionaryItemsDto) => {
  if (fee?.feeApplicableLevelType === FeeApplicableLevelType.FLOOR_PLAN && fee?.floorplan.length > 0) {
    const floorplanNames = fee.floorplan
      .map((floorplanId) => dictionaryItems?.floorplan[floorplanId]?.feedModelName)
      .filter(Boolean);
    return floorplanNames.length > 0 ? floorplanNames.join(', ') : DEFAULT_APPLICABLE_LEVEL_DISPLAY;
  }

  if (fee?.feeApplicableLevelType === FeeApplicableLevelType.UNIT && fee?.unit.length > 0) {
    const unitNames = fee.unit.map((unitId) => dictionaryItems?.unit[unitId]?.feedModelName).filter(Boolean);
    return unitNames.length > 0 ? unitNames.join(', ') : DEFAULT_APPLICABLE_LEVEL_DISPLAY;
  }

  return DEFAULT_APPLICABLE_LEVEL_DISPLAY;
};

const MultifamilyViewFeeDetailsModal: React.FC<MultifamilyViewFeeDetailsModalProps> = ({
  onClose,
  fee,
  dictionaryItems,
  onEditFee,
  isEditFeeDisabled,
}) => {
  if (!dictionaryItems) return null;

  const createdDate = fee.feeCreatedMs ? new Date(Number(fee.feeCreatedMs)) : null;
  const createdDateStr = createdDate && !isNaN(createdDate.getTime()) ? createdDate.toLocaleDateString() : '';

  const description =
    fee.isImportedFromFeed && fee.feeDisplayDescription ? fee.feeDisplayDescription : fee.feeDescription;

  const showDescription = !!description;
  const showFeedName = fee.isImportedFromFeed && !!fee.feeName;
  const showInternalCode = !!fee.feeCategory;
  const showFeeAmount = !!fee.feeAmountDisplay;

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && onClose()}
      header={<Heading level={2}>Fee details</Heading>}
      body={
        <Box
          css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', rowGap: '400' }}
          data-testid="MultifamilyViewFeeDetailsModal"
        >
          <Box css={detailLabelStyles}>
            <Text textStyle="body-bold">Category</Text>
          </Box>
          <Box css={detailValueStyles}>
            <Box css={categoryValueStyles}>
              {(() => {
                const categoryData = dictionaryItems.feeCategory[fee.feeCategory];
                const IconComponent = categoryData?.feeCategoryIconName
                  ? getConstellationIcon({ iconName: categoryData.feeCategoryIconName })
                  : null;
                return IconComponent ? <Icon size="md" render={<IconComponent />} /> : null;
              })()}
              <Text>{getDisplayValue(dictionaryItems.feeCategory, fee.feeCategory)}</Text>
            </Box>
          </Box>

          <Box css={detailLabelStyles}>
            <Text textStyle="body-bold">Fee name</Text>
          </Box>
          <Box css={detailValueStyles}>
            <Text>{getFeeNameDisplay(fee, dictionaryItems)}</Text>
          </Box>

          {showDescription && (
            <>
              <Box css={detailLabelStyles}>
                <Text textStyle="body-bold">Description</Text>
              </Box>
              <Box css={detailValueStyles}>
                <Text>{description}</Text>
              </Box>
            </>
          )}

          <Box css={detailLabelStyles}>
            <Text textStyle="body-bold">Source</Text>
          </Box>
          <Box css={detailValueStyles}>
            <Tag tone={fee.isImportedFromFeed ? 'gray' : 'aqua'} size="sm">
              {fee.isImportedFromFeed ? 'Imported from feed' : 'Added manually'}
            </Tag>
          </Box>

          {fee.isImportedFromFeed && showFeedName && (
            <>
              <Box css={detailLabelStyles}>
                <Text textStyle="body-bold">Feed name</Text>
              </Box>
              <Box css={detailValueStyles}>
                <Text>{fee.feeName}</Text>
              </Box>
            </>
          )}

          {fee.isImportedFromFeed && showInternalCode && (
            <>
              <Box css={detailLabelStyles}>
                <Text textStyle="body-bold">Internal code</Text>
              </Box>
              <Box css={detailValueStyles}>
                <Text>{getDisplayValue(dictionaryItems.feeCategory, fee.feeCategory)}</Text>
              </Box>
            </>
          )}

          {!fee.isImportedFromFeed && (
            <>
              <Box css={detailLabelStyles}>
                <Text textStyle="body-bold">Created date</Text>
              </Box>
              <Box css={detailValueStyles}>
                <Text>{createdDateStr}</Text>
              </Box>
            </>
          )}

          <Box css={detailLabelStyles}>
            <Text textStyle="body-bold">Requirement</Text>
          </Box>
          <Box css={detailValueStyles}>
            <Text>{getDisplayValue(dictionaryItems.feeRequirementType, fee.feeRequirementType)}</Text>
          </Box>

          <Box css={detailLabelStyles}>
            <Text textStyle="body-bold">Refundable</Text>
          </Box>
          <Box css={detailValueStyles}>
            <Text>{getDisplayValue(dictionaryItems.feeRefundableType, fee.feeRefundableType)}</Text>
          </Box>

          <Box css={detailLabelStyles}>
            <Text textStyle="body-bold">Payment frequency</Text>
          </Box>
          <Box css={detailValueStyles}>
            <Text>{getDisplayValue(dictionaryItems.feePaymentFrequencyType, fee.feePaymentFrequencyType)}</Text>
          </Box>

          {fee.maxOccurrences && fee.maxOccurrences > 0 && (
            <>
              <Box css={detailLabelStyles}>
                <Text textStyle="body-bold">Max occurrences</Text>
              </Box>
              <Box css={detailValueStyles}>
                <Text>{fee.maxOccurrences.toString()}</Text>
              </Box>
            </>
          )}

          <Box css={detailLabelStyles}>
            <Text textStyle="body-bold">Applies to</Text>
          </Box>
          <Box css={detailValueStyles}>
            <Text>{getFloorPlanOrUnitDisplay(fee, dictionaryItems)}</Text>
          </Box>

          <Box css={detailLabelStyles}>
            <Text textStyle="body-bold">Fee format</Text>
          </Box>
          <Box css={detailValueStyles}>
            <Text>{getDisplayValue(dictionaryItems.feeCalculationType, fee.feeCalculationType)}</Text>
          </Box>

          {showFeeAmount && (
            <>
              <Box css={detailLabelStyles}>
                <Text textStyle="body-bold">Fee amount</Text>
              </Box>
              <Box css={detailValueStyles}>
                <Text>{fee.feeAmountDisplay}</Text>
              </Box>
            </>
          )}
        </Box>
      }
      footer={
        <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <Button.Root
            data-testid="edit-fee-button"
            data-eventid={EVENT_IDS.CLICK_EDIT_MF_FEE}
            tone="brand"
            emphasis="outlined"
            disabled={isEditFeeDisabled}
            onClick={() => {
              onClose();
              onEditFee(fee);
            }}
          >
            <Button.Icon render={<IconEditFilled />} />
            <Button.Label>Edit fee</Button.Label>
          </Button.Root>
        </Box>
      }
      size={{ base: 'fullScreen', md: 'md' }}
    />
  );
};

export default MultifamilyViewFeeDetailsModal;
